import { ReactNode, useEffect, useRef, useState } from 'react';
import { LoadingSpinner } from './LoadingSpinner';
import { cn } from '@/lib/utils';

interface LazySectionProps {
  children: ReactNode;
  fallback?: ReactNode;
  rootMargin?: string;
  className?: string;
  threshold?: number;
}

export const LazySection = ({
  children,
  fallback,
  rootMargin = '100px',
  className,
  threshold = 0.01,
}: LazySectionProps) => {
  const [isVisible, setIsVisible] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !hasLoaded) {
            setIsVisible(true);
            setHasLoaded(true);
            // Once loaded, disconnect the observer
            observer.disconnect();
          }
        });
      },
      {
        rootMargin,
        threshold,
      }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      observer.disconnect();
    };
  }, [rootMargin, threshold, hasLoaded]);

  return (
    <div ref={sectionRef} className={cn('min-h-[100px]', className)}>
      {isVisible ? (
        <div className="animate-in fade-in duration-500">{children}</div>
      ) : (
        fallback || (
          <div className="flex items-center justify-center py-12">
            <LoadingSpinner size="md" />
          </div>
        )
      )}
    </div>
  );
};
